import type { UsageSeriesPoint } from "../api/usage.service";

interface UsageSeriesChartProps {
  points: UsageSeriesPoint[];
  isLoading?: boolean;
}

const W = 800;
const H = 180;
const ML = 46;
const MR = 12;
const MT = 12;
const MB = 26;

function formatarDia(iso: string): string {
  // `2026-08-12` → `12/08`. Sem `new Date` para não deslocar o dia por fuso: a
  // string já vem em UTC e o rótulo é só o dia.
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}

function formatarNumero(n: number): string {
  return n.toLocaleString("pt-BR");
}

/**
 * Volume diário de mensagens no período.
 *
 * Uma série só, então uma cor só e sem legenda — o título já diz o que é. O eixo
 * começa em zero porque a leitura da tela é de proporção entre dias, e um eixo
 * truncado exageraria variações pequenas.
 *
 * Desenhado em SVG direto: o manager não tem biblioteca de gráfico, e trazer uma
 * para um gráfico custa mais em peso de bundle do que o gráfico vale.
 */
export function UsageSeriesChart({ points, isLoading }: UsageSeriesChartProps) {
  if (isLoading) {
    return <div className="h-[180px] animate-pulse rounded-lg bg-gray-100" />;
  }

  if (points.length === 0) {
    return (
      <div className="flex h-[180px] items-center justify-center rounded-lg border border-dashed border-gray-200 text-sm text-gray-500">
        Sem consolidado no período selecionado.
      </div>
    );
  }

  const pw = W - ML - MR;
  const ph = H - MT - MB;
  const maxValor = Math.max(...points.map((p) => p.messagesTotal), 1);
  // Teto arredondado para cima, para o eixo cair em número redondo.
  const teto = Math.ceil(maxValor / 4) * 4;

  const x = (i: number) =>
    points.length === 1 ? ML + pw / 2 : ML + (i * pw) / (points.length - 1);
  const y = (v: number) => MT + ph - (v / teto) * ph;

  const linha = points
    .map(
      (p, i) =>
        `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.messagesTotal).toFixed(1)}`,
    )
    .join(" ");
  const area = `${linha} L${x(points.length - 1).toFixed(1)},${MT + ph} L${x(0).toFixed(1)},${MT + ph} Z`;

  // Com muitos dias, rotular todos vira borrão. Um a cada N mantém legível.
  const passo = Math.max(1, Math.ceil(points.length / 12));

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full"
      role="img"
      aria-label={`Volume diário de mensagens, de ${formatarDia(points[0].day)} a ${formatarDia(points[points.length - 1].day)}`}
    >
      {[0, 0.25, 0.5, 0.75, 1].map((f) => (
        <g key={f}>
          <line
            x1={ML}
            y1={y(teto * f)}
            x2={W - MR}
            y2={y(teto * f)}
            className="stroke-gray-200"
            strokeWidth={1}
          />
          <text
            x={ML - 8}
            y={y(teto * f) + 4}
            textAnchor="end"
            className="fill-gray-400 text-[10px]"
          >
            {formatarNumero(Math.round(teto * f))}
          </text>
        </g>
      ))}

      <path d={area} className="fill-violet-500/10" />
      <path
        d={linha}
        className="stroke-violet-600"
        strokeWidth={2}
        fill="none"
        strokeLinejoin="round"
      />

      {points.map((p, i) => (
        <g key={p.day}>
          <circle
            cx={x(i)}
            cy={y(p.messagesTotal)}
            r={3.5}
            className="fill-violet-600 stroke-white"
            strokeWidth={1.5}
          >
            <title>{`${formatarDia(p.day)} — ${formatarNumero(p.messagesTotal)} mensagens, ${formatarNumero(p.mediaCount)} com mídia`}</title>
          </circle>
          {i % passo === 0 && (
            <text
              x={x(i)}
              y={H - 8}
              textAnchor="middle"
              className="fill-gray-400 text-[10px]"
            >
              {formatarDia(p.day)}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}
